import React from "react";
import { useI18n } from "../i18n/LanguageContext";
import { ExternalLink, Calculator, Info } from "lucide-react";

const CALCULATORS = [
  {
    region: "FR",
    country: "France",
    flag: "🇫🇷",
    title: "Simulateur — coût du certificat d'immatriculation",
    titleDe: "Simulator — Kosten der Zulassungsbescheinigung",
    titleEn: "Simulator — vehicle registration cost",
    org: "Service-Public.gouv.fr",
    desc: "Simulateur officiel de l'État français pour calculer le coût total de votre carte grise selon la région, la puissance fiscale, l'énergie et le type de démarche.",
    descDe: "Offizieller Simulator des französischen Staates zur Berechnung der Gesamtkosten der Zulassungsbescheinigung je nach Region, Steuerleistung, Energie und Verfahren.",
    descEn: "Official French government simulator to compute the total cost of your registration certificate based on region, fiscal power, fuel type and procedure.",
    url: "https://www.service-public.gouv.fr/simulateur/calcul/cout-certificat-immatriculation",
    domain: "service-public.gouv.fr",
  },
  {
    region: "BE-W",
    country: "Wallonie",
    flag: "🇧🇪",
    title: "Simulateur — Taxe de mise en circulation Wallonie",
    titleDe: "Simulator — Erstzulassungssteuer Wallonie",
    titleEn: "Simulator — Walloon registration tax",
    org: "SPW Finances",
    desc: "Simulateur officiel du Service Public de Wallonie pour estimer la taxe de mise en circulation (TMC) des véhicules immatriculés en Région wallonne. Barème CO₂ + masse + cylindrée.",
    descDe: "Offizieller Simulator des SPW zur Schätzung der Erstzulassungssteuer für in der Wallonie zugelassene Fahrzeuge (Tarif CO₂ + Masse + Hubraum).",
    descEn: "Official Walloon Public Service simulator to estimate the registration tax (TMC) for vehicles registered in the Walloon Region (CO₂ + mass + displacement scale).",
    url: "https://finances.wallonie.be/home/fiscalite/taxe-de-mise-en-circulation.html",
    domain: "finances.wallonie.be",
  },
  {
    region: "BE-BXL",
    country: "Bruxelles",
    flag: "🇧🇪",
    title: "MyTax — Taxes de circulation Bruxelles",
    titleDe: "MyTax — Verkehrssteuern Brüssel",
    titleEn: "MyTax — Brussels circulation taxes",
    org: "Bruxelles Fiscalité · fisc.brussels",
    desc: "Plateforme officielle MyTax Brussels pour calculer la TMC et la taxe de circulation annuelle des véhicules domiciliés en Région de Bruxelles-Capitale.",
    descDe: "Offizielle Plattform MyTax Brussels zur Berechnung der TMC und der jährlichen Verkehrssteuer für Fahrzeuge in der Region Brüssel-Hauptstadt.",
    descEn: "Official MyTax Brussels platform to compute the TMC and annual circulation tax for vehicles domiciled in the Brussels-Capital Region.",
    url: "https://fisc.brussels/mytax/fr/tc",
    domain: "fisc.brussels",
  },
  {
    region: "BE-VL",
    country: "Vlaanderen",
    flag: "🇧🇪",
    title: "Simulator — Belasting op de Inverkeerstelling (BIV)",
    titleDe: "Simulator — Erstzulassungssteuer Flandern (BIV)",
    titleEn: "Simulator — Flemish registration tax (BIV)",
    org: "Vlaamse Belastingdienst",
    desc: "Simulateur officiel du gouvernement flamand pour calculer la BIV (équivalent flamand de la TMC). Prise en compte des émissions CO₂, de la norme Euro et du carburant.",
    descDe: "Offizieller Simulator der flämischen Regierung zur Berechnung der BIV (flämisches Äquivalent zur TMC) unter Berücksichtigung von CO₂-Emissionen, Euro-Norm und Kraftstoff.",
    descEn: "Official Flemish government simulator to compute the BIV (Flemish equivalent of the TMC), accounting for CO₂ emissions, Euro standard and fuel type.",
    url: "https://www.vlaanderen.be/belastingen-en-begroting/vlaamse-belastingen/verkeersbelastingen/belasting-op-de-inverkeerstelling/berekening-van-de-belasting-op-de-inverkeerstelling",
    domain: "vlaanderen.be",
  },
];

export default function Calculators() {
  const { t, lang } = useI18n();

  const titleKey = lang === "de" ? "titleDe" : lang === "en" ? "titleEn" : "title";
  const descKey = lang === "de" ? "descDe" : lang === "en" ? "descEn" : "desc";

  return (
    <div data-testid="page-calculators" className="bg-ct3f-bg text-white">
      {/* HERO */}
      <section className="border-b border-white/10 bg-flame-radial">
        <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-28 relative">
          <div className="absolute inset-0 grid-lines pointer-events-none opacity-50" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <span className="label-tech text-ct3f-amber">{t.calculators.eyebrow}</span>
              <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl lg:text-8xl mt-4 leading-[0.95]">
                <span className="text-white">{t.calculators.title}</span>
                <span className="text-flame">.</span>
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/70 text-lg max-w-md">{t.calculators.sub}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CARDS */}
      <section className="border-b border-white/10">
        <div className="px-6 md:px-12 lg:px-20 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/10">
            {CALCULATORS.map((c, i) => (
              <a
                key={c.region}
                href={c.url}
                target="_blank"
                rel="noreferrer noopener"
                data-testid={`calc-card-${c.region}`}
                data-umami-event="calculator-click"
                data-umami-event-region={c.region}
                className="group relative p-8 lg:p-10 border-r border-b border-white/10 bg-ct3f-surface hover:bg-ct3f-elevated transition-colors flex flex-col"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="label-tech border border-ct3f-orange/60 text-ct3f-amber px-2 py-1">
                    [ {c.region} ]
                  </span>
                  <Calculator
                    size={28}
                    strokeWidth={1.4}
                    className="text-white/35 group-hover:text-ct3f-orange transition-colors"
                  />
                </div>

                <div className="label-tech text-white/45 mb-2">{c.country}</div>
                <h2 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-white leading-tight">
                  {c[titleKey]}
                </h2>
                <p className="mt-2 label-tech text-white/40">{c.org}</p>
                <p className="mt-5 text-white/65 leading-relaxed">{c[descKey]}</p>

                <div className="mt-auto pt-6 flex items-center justify-between">
                  <span className="label-tech text-white/35 truncate max-w-[60%]">
                    {c.domain}
                  </span>
                  <span className="inline-flex items-center gap-2 label-tech text-ct3f-amber group-hover:text-white transition-colors">
                    {t.calculators.openLink}
                    <ExternalLink size={14} className="hover-arrow" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="border-b border-white/10 bg-ct3f-bg">
        <div className="px-6 md:px-12 lg:px-20 py-12 lg:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start max-w-5xl">
            <div className="lg:col-span-1">
              <Info size={28} strokeWidth={1.4} className="text-ct3f-amber" />
            </div>
            <div className="lg:col-span-11">
              <span className="label-tech text-ct3f-amber">{t.calculators.disclaimerLabel}</span>
              <p className="mt-3 text-white/65 leading-relaxed max-w-3xl">
                {t.calculators.disclaimer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-flame text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 mix-blend-overlay"
          style={{
            background:
              "radial-gradient(circle at 10% 90%, rgba(0,0,0,0.4), transparent 55%)",
          }}
        />
        <div className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div>
            <span className="label-tech text-white/85">{t.calculators.ctaEyebrow}</span>
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl lg:text-5xl mt-3">
              {t.calculators.ctaTitle}.
            </h2>
          </div>
          <a
            href="/contact"
            data-testid="calculators-cta"
            className="group inline-flex items-center gap-3 bg-ct3f-bg text-white label-tech px-8 py-5 hover:bg-black transition-colors"
          >
            {t.nav.cta}
            <ExternalLink size={18} className="hover-arrow rotate-[-45deg]" />
          </a>
        </div>
      </section>
    </div>
  );
}
